import {Alert, Form, Input, notification, Select} from "@ui";
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import {adapterBankList, findBankData} from "../../../adapters/bankListAdapter";
import usePagoMovilRegistration from "../../../hooks/usePagoMovilRegistration";
import usePutUserPagoMovilRegistrationData from "../../../hooks/usePutUserPagoMovilRegistrationData.hook";
import useUpdateProfile from "../../../hooks/useUpdateProfile";
import useCreateAccPagoMovil from "../../../hooks/wallet/useCreateAccPagoMovil";
import useEditAccPagoMovil from "../../../hooks/wallet/useEditAccPagoMovil";
// import PagoMovilRegistrationFields from "../PagoMovilRegistrationFields/index.jsx";
import styles from "./AddPagoMovil.module.css";

const AddPagoMovilForm = ({isUpdate, data, onSuccess, onCancel}) => {
  const {mutate, isPending, isSuccess, isError, error} = useCreateAccPagoMovil();
  const {update, isPendingUpdate, isSuccessUpdate} = useEditAccPagoMovil();
  const {needsRegistration, userData, hasNationalID, hasPhoneNumber, executePendingAction} = usePagoMovilRegistration();

  // Hook for full registration (profile + pago movil data)
  const {
    putUserPagoMovilData,
    isPending: isPendingFullRegistration,
    isSuccess: isSuccessFullRegistration,
    error: errorFullRegistration,
  } = usePutUserPagoMovilRegistrationData();

  // Hook for updating user profile information (when user has some data but missing some fields)
  const {
    postUpdate,
    isPending: isPendingProfile,
    isSuccess: isSuccessProfile,
  } = useUpdateProfile(() => {
    // After profile update is successful, we need to trigger the Pago Móvil account creation
    // This will be handled in the useEffect below
  });

  const {t} = useTranslation();

  const [form] = Form.useForm();
  const [showConfirmOverlay, setShowConfirmOverlay] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  // Determine which form fields to show and which registration approach to use
  const showFullRegistrationForm = !isUpdate && needsRegistration;
  const needsFullRegistration = !hasNationalID || !hasPhoneNumber;

  // Show error message when nationalID or phone number is duplicated during full registration
  useEffect(() => {
    if (errorFullRegistration) {
      const errorMessage = errorFullRegistration.message;
      if (errorMessage.includes("duplicated.attribute.nationalID")) {
        form.setFields([
          {
            name: "nationalID",
            errors: ["Este número de cédula ya está registrado."],
          },
        ]);
      } else if (errorMessage.includes("duplicated.attribute.phoneNumber")) {
        form.setFields([
          {
            name: "phoneNumber",
            errors: ["Este número de teléfono ya está registrado."],
          },
        ]);
      } else {
        notification.error({
          message: t("pagoMovilRegistrationErrorTitle") || "Error",
          description: "Hubo un error durante el registro. Por favor, inténtalo de nuevo.",
        });
      }
    }
  }, [errorFullRegistration, form, t]);

  // Show error notification when pago movil creation fails
  useEffect(() => {
    if (isError && error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || "";

      notification.error({
        message: t("pagoMovilCreationErrorTitle") || "Error",
        description:
          errorMessage ||
          t("pagoMovilCreationErrorMessage") ||
          "No se pudo agregar la cuenta de pago móvil. Inténtalo de nuevo.",
      });
    }
  }, [isError, error, t]);

  // Reset form when component unmounts or when switching between different modal states
  useEffect(() => {
    return () => {
      // Cleanup function to reset form when component unmounts
      form.resetFields();
    };
  }, [form]);

  // Use useEffect to set initial values without triggering re-renders
  useEffect(() => {
    if (isUpdate) {
      // Find the bank data based on the bank code
      const bankData = findBankData({bank_code: data.bank_code});
      form.setFieldsValue({
        ID: data.ID, // Add ID field
        bank: {value: data.bank_code, label: bankData.bank_name},
        phone: data.phone_number,
      });
    } else if (showFullRegistrationForm && userData) {
      // Pre-populate with existing user data if available
      form.setFieldsValue({
        firstName: userData.given_name || userData.firstName || "",
        lastName: userData.family_name || userData.lastName || "",
        nationalID: userData.nationalID || "",
        phoneNumber: userData.phoneNumber || "",
        phone: userData.phoneNumber || "", // Also populate phone field for Pago Móvil creation
      });
    }
  }, [isUpdate, data, form, showFullRegistrationForm, userData]); // Include dependencies for useEffect

  const handleSubmit = values => {
    try {
      // For updates, show confirmation overlay first
      if (isUpdate) {
        setPendingFormValues(values);
        setShowConfirmOverlay(true);
        return;
      }

      // For non-updates, proceed directly
      executeSubmit(values);
    } catch (_error) {
      notification.error({
        message: "Error",
        description: "There was an error processing your request. Please try again.",
      });
    }
  };

  const executeSubmit = values => {
    try {
      // Combine ID prefix and number for backend
      const fullNationalID = values.idPrefix ? `${values.idPrefix}${values.nationalID}` : values.nationalID;

      if (showFullRegistrationForm) {
        if (needsFullRegistration) {
          // Use the combined hook for full registration (profile data only)
          putUserPagoMovilData({
            firstName: values.firstName,
            lastName: values.lastName,
            nationalID: fullNationalID,
            phoneNumber: values.phoneNumber,
          });

          // Store bank and phone values for later use after profile update
          form.setFieldsValue({
            ...values,
            _bankForPagoMovil: values.bank,
            _phoneForPagoMovil: values.phoneNumber,
          });
        } else {
          const profileData = {
            firstName: values.firstName,
            lastName: values.lastName,
            nationalID: fullNationalID,
            phoneNumber: values.phoneNumber,
          };

          form.setFieldsValue({
            ...values,
            _bankForPagoMovil: values.bank,
            _phoneForPagoMovil: values.phoneNumber,
          });

          postUpdate(profileData);
        }
      } else {
        // Handle simple form (just pago movil info)
        let bankCode = null;

        if (values.bank.value === undefined) {
          bankCode = values.bank;
        } else {
          bankCode = values.bank.value;
        }

        if (isUpdate) {
          update({
            ID: values.ID,
            bank_code: Number(bankCode),
            phone_number: Number(values.phone),
          });
        } else {
          mutate({
            bank_code: Number(bankCode),
            phone_number: Number(values.phone),
          });
        }
      }
    } catch (_error) {
      notification.error({
        message: "Error",
        description: "There was an error processing your request. Please try again.",
      });
    }
  };

  const handleConfirmUpdate = () => {
    setShowConfirmOverlay(false);
    executeSubmit(pendingFormValues);
    setPendingFormValues(null);
  };

  const handleCancelUpdate = () => {
    setShowConfirmOverlay(false);
    setPendingFormValues(null);
  };

  // Handle profile update success - then create Pago Móvil account
  useEffect(() => {
    try {
      if ((isSuccessProfile || isSuccessFullRegistration) && showFullRegistrationForm) {
        const formValues = form.getFieldsValue();
        const bankCode = formValues._bankForPagoMovil?.value ?? formValues._bankForPagoMovil ?? formValues.bank;

        // Use phoneNumber field since that's the input field name in full registration form
        const phoneNumber = formValues._phoneForPagoMovil || formValues.phoneNumber;

        mutate({
          bank_code: Number(bankCode),
          phone_number: Number(phoneNumber),
        });
      }
    } catch (_error) {
      // Handle error silently
    }
  }, [isSuccessProfile, isSuccessFullRegistration, showFullRegistrationForm, form, mutate]);

  useEffect(() => {
    try {
      // Handle success notifications - only show one notification per successful operation
      if (isSuccessUpdate) {
        notification.success({
          message: "Pago movil actualizado correctamente",
        });
        form.resetFields(); // Reset form after successful update
        if (onSuccess) {
          onSuccess();
        }
      } else if (isSuccess && !showFullRegistrationForm) {
        // Simple Pago Movil creation (no full registration)
        notification.success({
          message: "Pago movil agregado correctamente",
        });
        form.resetFields(); // Reset form after successful creation
        if (onSuccess) {
          onSuccess();
        }
      } else if (isSuccess && showFullRegistrationForm) {
        // Full registration completed successfully (profile + pago movil)
        notification.success({
          message: t("pagoMovilRegistrationSuccessTitle") || "Registration Successful",
          description:
            t("pagoMovilRegistrationSuccessMessage") ||
            "Your personal information and Pago Móvil account have been registered successfully.",
        });
        form.resetFields(); // Reset form after successful registration

        if (onSuccess) {
          onSuccess();
        }

        // Execute pending action after modal closes
        const timeoutId = setTimeout(async () => {
          try {
            if (executePendingAction && typeof executePendingAction === "function") {
              await executePendingAction();
            } else {
            }
          } catch (_error) {
            // Don't throw the error to prevent it from bubbling up to Error Boundary
          }
        }, 300); // Small delay to ensure modal closes first

        // Cleanup timeout if component unmounts
        return () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        };
      }
    } catch (_error) {}
  }, [isSuccess, isSuccessUpdate, showFullRegistrationForm, t, onSuccess, form]);

  return (
    <>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        {/* Show explanation message when user needs full registration */}
        {showFullRegistrationForm && (
          <Alert
            message={t("addPagoMovilExplanationTitle") || "Personal Registration Required"}
            description={t("addPagoMovilExplanationMessage")}
            type="warning"
            showIcon
            className={styles.registrationAlert}
          />
        )}

        {isUpdate && ( // Only render ID field if isUpdate is true
          <Form.Item
            name="ID"
            label="ID"
            hidden // Hide the ID field visually
          >
            <Input />
          </Form.Item>
        )}

        {/* Show personal information fields only for full registration */}
        {showFullRegistrationForm && (
          <>
            <Form.Item
              name="firstName"
              label={t("name") || "Name"}
              rules={[
                {
                  required: true,
                  message: t("nameRequired") || "Name is required",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={t("lastName") || "Last Name"}
              rules={[
                {
                  required: true,
                  message: t("lastNameRequired") || "Last name is required",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item label={t("nationalID")}>
              <Input.Group compact>
                <Form.Item
                  name="idPrefix"
                  noStyle
                  initialValue="V-"
                >
                  <Select
                    style={{width: 50}}
                    className="id-prefix-select"
                  >
                    <Select.Option value="V-">V</Select.Option>
                    <Select.Option value="E-">E</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="nationalID"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("nationalIDRequired") || "ID is required",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }

                        // Check if value contains only numbers
                        if (!/^\d+$/.test(value)) {
                          return Promise.reject(new Error("Only numbers are allowed"));
                        }

                        // Check minimum length (7 digits)
                        if (value.length < 7) {
                          return Promise.reject(
                            new Error(t("pagoMovilRegistrationCedulaMinLength") || "ID must have at least 7 digits")
                          );
                        }

                        // Check maximum length (15 digits)
                        if (value.length > 15) {
                          return Promise.reject(
                            new Error(t("pagoMovilRegistrationCedulaMaxLength") || "ID cannot have more than 15 digits")
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input style={{width: "calc(100% - 50px)"}} />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label={t("phoneNumber") || "Phone Number"}
              rules={[
                {
                  required: true,
                  message: t("phoneNumberRequired") || "Phone number is required",
                },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }

                    // Check if value contains only numbers
                    if (!/^\d+$/.test(value)) {
                      return Promise.reject(new Error("Only numbers are allowed"));
                    }

                    // Check minimum length (10 digits for Venezuelan phone numbers)
                    if (value.length < 10) {
                      return Promise.reject(
                        new Error(t("phoneNumberMinLength") || "Phone number must have at least 10 digits")
                      );
                    }

                    // Check maximum length (11 digits)
                    if (value.length > 11) {
                      return Promise.reject(
                        new Error(t("phoneNumberMaxLength") || "Phone number cannot have more than 11 digits")
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="bank"
          label={
            <span>
              {t("mobilePaymentModalBank")}
              <span style={{color: "var(--status-error)", marginLeft: "4px"}}>*</span>
            </span>
          }
          rules={[
            {
              required: true,
              message: t("mobilePaymentModalSelectBankMessage"),
            },
          ]}
          required={false} // Disable default asterisk
        >
          <Select
            showSearch
            placeholder={t("mobilePaymentModalSelectBank")}
            optionFilterProp="children"
            filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            {adapterBankList().map(item => (
              <Select.Option
                key={item.value}
                value={item.value}
              >
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Show phone field only when NOT in full registration mode */}
        {!showFullRegistrationForm && (
          <Form.Item
            name="phone"
            label={
              <span>
                {t("mobilePaymentModalPhone")}
                <span style={{color: "var(--status-error)", marginLeft: "4px"}}>*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: t("mobilePaymentModalPhonePlaceholderMessage"),
              },
            ]}
            required={false} // Disable default asterisk
          >
            <Input placeholder={t("mobilePaymentModalPhonePlaceholder")} />
          </Form.Item>
        )}

        <Form.Item style={{marginBottom: 0}}>
          <div className="form-modal-footer">
            {onCancel && <SecondaryButton onClick={onCancel}>{t("cancel") || "Cancelar"}</SecondaryButton>}
            <PrimaryButton
              htmlType="submit"
              loading={isUpdate ? isPendingUpdate : isPending || isPendingProfile || isPendingFullRegistration}
            >
              {showFullRegistrationForm
                ? t("registerAndAddPagoMovil") || "Register & Add Mobile Payment"
                : t("mobilePaymentModalSubmitBtn")}
            </PrimaryButton>
          </div>
        </Form.Item>
      </Form>

      {showConfirmOverlay && (
        <div
          className={styles.confirmOverlay}
          onClick={handleCancelUpdate}
        >
          <div
            className={styles.confirmCard}
            onClick={e => e.stopPropagation()}
          >
            <span className={styles.confirmText}>
              {t("confirmSaveChanges") || "¿Estás seguro de que quieres guardar los cambios?"}
            </span>
            <div className="form-modal-footer">
              <SecondaryButton onClick={handleCancelUpdate}>{t("cancel") || "Cancelar"}</SecondaryButton>
              <PrimaryButton
                onClick={handleConfirmUpdate}
                loading={isPendingUpdate}
              >
                {t("accept") || "Aceptar"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AddPagoMovilForm.propTypes = {
  isUpdate: PropTypes.bool,
  data: PropTypes.object,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default AddPagoMovilForm;
